/**
 * PocketFiler - Setup Checklist Widget
 * Shown on dashboard until user completes onboarding or dismisses.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import api from '../lib/api';
import { Check, X, ArrowRight, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

export default function SetupChecklist() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const data = await api.get('/onboarding/status');
      setStatus(data);
      if (data.onboarding_dismissed) setDismissed(true);
    } catch (e) {}
  };

  const handleItemClick = (item) => {
    if (item.link) {
      navigate(item.link);
    }
  };

  const handleDismiss = async () => {
    if (!window.confirm(t('onboarding.checklist.confirmDismiss') || 'Hide the setup checklist? You can still access it from Settings.')) return;
    try {
      await api.post('/onboarding/dismiss');
      setDismissed(true);
    } catch (e) {}
  };

  if (!status || dismissed) return null;
  if (!status.checklist_items || status.checklist_items.length === 0) return null;

  const { progress } = status;
  const isComplete = progress.percent === 100;

  // Hide if 100% complete (user finished or already onboarded)
  if (isComplete && status.onboarding_completed_at) return null;

  return (
    <div className="rounded-xl border bg-gradient-to-br from-primary/5 to-background p-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold">
              {t('onboarding.checklist.title') || 'Setup Progress'}
            </h3>
            <p className="text-xs text-muted-foreground">
              {progress.completed} {t('onboarding.checklist.of') || 'of'} {progress.total} {t('onboarding.checklist.complete') || 'complete'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 text-muted-foreground hover:text-foreground rounded"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={handleDismiss}
            className="p-1.5 text-muted-foreground hover:text-foreground rounded"
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden mb-3">
        <div
          className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all"
          style={{ width: `${progress.percent}%` }}
        />
      </div>

      {/* Items */}
      {expanded && (
        <div className="space-y-1.5">
          {status.checklist_items.map((item) => {
            const done = !!status.checklist?.[item.key];
            return (
              <div
                key={item.key}
                onClick={() => !done && handleItemClick(item)}
                className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                  done
                    ? 'text-muted-foreground'
                    : 'hover:bg-primary/5 cursor-pointer'
                }`}
              >
                <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  done
                    ? 'bg-primary border-primary text-primary-foreground'
                    : 'border-gray-300 dark:border-gray-700'
                }`}>
                  {done && <Check className="h-3 w-3" />}
                </div>
                <span className={`text-sm flex-1 ${done ? 'line-through' : ''}`}>
                  {t(`onboarding.checklist.${item.key}`) || item.label}
                </span>
                {!done && item.link && (
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {isComplete && (
        <div className="mt-3 p-2 rounded-lg bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 text-sm text-center font-medium">
          🎉 {t('onboarding.checklist.allDone') || 'All set! You\'re ready to go.'}
        </div>
      )}
    </div>
  );
}
